import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { ArrowLeft, Bell, Pin, PinOff, Plus, Trash2, Check, AlertTriangle, Info, AlertCircle, Edit3, X, Phone, Mail, Users, CreditCard, ChevronDown } from "lucide-react";
import CopyableField from "@/components/CopyableField";
import { ClientStatusPill, AlertSeverityPill } from "@/components/StatusPill";
import UserAssignSelect from "@/components/UserAssignSelect";
import MentionTextarea from "@/components/MentionTextarea";
import ClientActionRunsPanel from "@/components/ClientActionRunsPanel";
import ClientCommunicationTab from "@/components/client/ClientCommunicationTab";
import ClientDocumentsTab from "@/components/client/ClientDocumentsTab";
import ClientEmploymentTab from "@/components/client/ClientEmploymentTab";
import ClientLoansTab from "@/components/client/ClientLoansTab";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { toast } from "sonner";

const STATUSES = ["active", "pending_docs", "submitted", "monitoring", "past_due", "cancelled", "completed", "inactive"];
const PAYMENT_STATUSES = [
    { value: "active", label: "Active" },
    { value: "nsf", label: "NSF" },
    { value: "invalid_card", label: "Invalid Card" },
    { value: "chargeback", label: "Chargeback" },
];
const PROGRAMS = ["unknown", "IDR", "IBR", "PAYE", "ICR", "Standard", "Other"];
const NOTE_CATEGORIES = ["General", "Payment", "Loan", "Document", "Follow-up", "Important"];

function SeverityIcon({ severity }) {
    if (severity === "urgent") return <AlertCircle className="h-4 w-4 text-rose-600" />;
    if (severity === "warning") return <AlertTriangle className="h-4 w-4 text-amber-600" />;
    return <Info className="h-4 w-4 text-blue-600" />;
}

const SEV_CARD_CLS = {
    urgent: "card-urgent",
    warning: "card-warning",
    info: "card-info",
};

export default function ClientDetail() {
    const { id } = useParams();
    const [data, setData] = useState(null);
    const [searchParams, setSearchParams] = useSearchParams();
    const [tab, setTab] = useState(searchParams.get("tab") || "overview");

    useEffect(() => {
        const wanted = searchParams.get("tab");
        if (wanted && wanted !== tab) setTab(wanted);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [searchParams]);
    useEffect(() => {
        const current = searchParams.get("tab");
        if (tab !== current) {
            const next = new URLSearchParams(searchParams);
            if (tab === "overview") next.delete("tab"); else next.set("tab", tab);
            setSearchParams(next, { replace: true });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    const mentionCandidates = useMemo(() => {
        const _client = data?.client;
        if (!_client) return [];
        const list = [];
        if (_client.processor_user_id && _client.processor_name) {
            list.push({ id: _client.processor_user_id, name: _client.processor_name, email: _client.processor_email, label: `Processor — ${_client.processor_email || ""}` });
        }
        if (_client.sales_rep_user_id && _client.sales_rep_name && _client.sales_rep_user_id !== _client.processor_user_id) {
            list.push({ id: _client.sales_rep_user_id, name: _client.sales_rep_name, email: _client.sales_rep_email, label: `Sales Rep — ${_client.sales_rep_email || ""}` });
        }
        return list;
    }, [data]);
    const [notes, setNotes] = useState([]);
    const [alerts, setAlerts] = useState([]);
    const [history, setHistory] = useState([]);
    const [addNoteOpen, setAddNoteOpen] = useState(false);
    const [addAlertOpen, setAddAlertOpen] = useState(false);
    const [editingNote, setEditingNote] = useState(null);
    const [editingAlert, setEditingAlert] = useState(null);
    const [noteForm, setNoteForm] = useState({ note_text: "", category: "General", pinned: false });
    const [noteMentions, setNoteMentions] = useState([]);
    const [alertForm, setAlertForm] = useState({ alert_title: "", alert_body: "", severity: "warning" });

    // Phase 2 — inline compose-at-top of the Notes tab.
    const [inlineNote, setInlineNote] = useState({ note_text: "", category: "General", pinned: false });
    const [inlineNoteMentions, setInlineNoteMentions] = useState([]);
    const [inlineBusy, setInlineBusy] = useState(false);

    const [editOverview, setEditOverview] = useState(false);
    const [overviewDraft, setOverviewDraft] = useState({});

    const load = useCallback(async () => { const d = await api.get(`/clients/${id}`); setData(d.data); }, [id]);
    const loadNotes = useCallback(async () => { const r = await api.get(`/clients/${id}/notes`); setNotes(r.data || []); }, [id]);
    const loadAlerts = useCallback(async () => { const r = await api.get(`/clients/${id}/alerts`); setAlerts(r.data || []); }, [id]);
    const loadHistory = useCallback(async () => { const r = await api.get(`/clients/${id}/history`); setHistory(r.data || []); }, [id]);
    const [loanSnap, setLoanSnap] = useState(null);
    const loadLoanSnap = useCallback(async () => {
        try { const r = await api.get(`/clients/${id}/loans-snapshot`); setLoanSnap(r.data || null); }
        catch (_) { /* non-fatal */ }
    }, [id]);

    useEffect(() => { load(); loadNotes(); loadAlerts(); loadHistory(); loadLoanSnap(); },
        [id, load, loadNotes, loadAlerts, loadHistory, loadLoanSnap]);

    if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
    const { client, active_alerts: activeAlerts, stats, employment_snapshot: empSnap } = data;

    const persist = async (patch, success = "Saved") => {
        try {
            await api.put(`/clients/${id}`, { ...client, ...patch });
            toast.success(success);
            await load();
            await loadHistory();
            return true;
        } catch (e) { toast.error(formatApiError(e)); return false; }
    };
    const updateStatus = async (v) => persist({ client_status: v }, "Status updated");
    const updatePaymentStatus = async (v) => {
        const label = PAYMENT_STATUSES.find(p => p.value === v)?.label || v;
        return persist({ payment_status: v }, `Payment Status: ${label}`);
    };

    const startEditOverview = () => {
        setOverviewDraft({
            // Identity
            case_id: client.case_id || "",
            first_name: client.first_name || "",
            last_name: client.last_name || "",
            dob: client.dob || "",
            ssn_last4: client.ssn_last4 || "",
            client_status: client.client_status || "active",
            program_type: client.program_type || "unknown",
            assigned_rep: client.assigned_rep || "",
            processor_user_id: client.processor_user_id || null,
            processor_name: client.processor_name || "",
            processor_email: client.processor_email || "",
            sales_rep_user_id: client.sales_rep_user_id || null,
            sales_rep_name: client.sales_rep_name || "",
            sales_rep_email: client.sales_rep_email || "",
            // Contact
            phone_raw: client.phone_raw || "",
            alternate_phone: client.alternate_phone || "",
            email: client.email || "",
            alternate_email: client.alternate_email || "",
            preferred_contact_method: client.preferred_contact_method || "",
            best_time_to_contact: client.best_time_to_contact || "",
            timezone: client.timezone || "",
            language: client.language || "",
            // Address
            address_line_1: client.address_line_1 || client.address || "",
            address_line_2: client.address_line_2 || "",
            city: client.city || "",
            state: client.state || "",
            zip: client.zip || "",
            county: client.county || "",
            // Case management
            service_type: client.service_type || "",
            enrolled_date: client.enrolled_date || "",
            program_start_date: client.program_start_date || "",
            recertification_due_date: client.recertification_due_date || "",
            renewal_due_date: client.renewal_due_date || "",
            important_deadline: client.important_deadline || "",
            internal_warning_notes: client.internal_warning_notes || "",
            preferred_contact_notes: client.preferred_contact_notes || "",
            // Restrictions
            do_not_call: !!client.do_not_call,
            do_not_text: !!client.do_not_text,
            do_not_email: !!client.do_not_email,
            do_not_mail: !!client.do_not_mail,
            compliance_notes: client.compliance_notes || "",
        });
        setEditOverview(true);
    };
    const saveOverview = async () => {
        const payload = { ...overviewDraft };
        // Last-4 SSN scrub (server enforces too).
        if (payload.ssn_last4) payload.ssn_last4 = String(payload.ssn_last4).replace(/\D/g, "").slice(-4);
        if (await persist(payload, "Overview saved")) setEditOverview(false);
    };

    const submitNote = async () => {
        try {
            const ids = Array.from(new Set(noteMentions.map(u => u.id)));
            if (editingNote) await api.put(`/clients/${id}/notes/${editingNote.id}`, noteForm);
            else await api.post(`/clients/${id}/notes`, { ...noteForm, mentioned_user_ids: ids });
            toast.success(editingNote ? "Note updated" : "Note added");
            setAddNoteOpen(false); setEditingNote(null);
            setNoteForm({ note_text: "", category: "General", pinned: false });
            setNoteMentions([]);
            // F4.1: nudge the bell right away
            window.dispatchEvent(new Event("notifications:refresh"));
            loadNotes(); loadHistory(); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const togglePin = async (n) => {
        try { await api.put(`/clients/${id}/notes/${n.id}`, { note_text: n.note_text, category: n.category, pinned: !n.pinned }); loadNotes(); }
        catch (e) { toast.error(formatApiError(e)); }
    };
    const deleteNote = async (n) => {
        if (!confirm("Delete this note?")) return;
        try { await api.delete(`/clients/${id}/notes/${n.id}`); loadNotes(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const submitAlert = async () => {
        try {
            if (editingAlert) await api.put(`/clients/${id}/alerts/${editingAlert.id}`, alertForm);
            else await api.post(`/clients/${id}/alerts`, alertForm);
            toast.success(editingAlert ? "Alert updated" : "Alert added");
            setAddAlertOpen(false); setEditingAlert(null);
            setAlertForm({ alert_title: "", alert_body: "", severity: "warning" });
            loadAlerts(); loadHistory(); load();
        } catch (e) { toast.error(formatApiError(e)); }
    };
    const resolveAlert = async (a) => {
        try { await api.post(`/clients/${id}/alerts/${a.id}/resolve`); toast.success("Alert resolved"); loadAlerts(); loadHistory(); load(); }
        catch (e) { toast.error(formatApiError(e)); }
    };

    const phone = client.phone_e164 || client.phone_raw;

    return (
        <div className="space-y-5" data-testid="client-detail-page">
            <Link to="/clients" className="inline-flex items-center text-sm text-muted-foreground hover:text-gray-900">
                <ArrowLeft className="h-4 w-4 mr-1" /> Back to clients
            </Link>

            {/* Header — control-panel layout */}
            <div className="card-elevated p-5 md:p-6">
                <div className="flex flex-wrap items-start justify-between gap-4">
                    <div className="space-y-3 min-w-0 flex-1">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-display font-semibold tracking-tight text-gray-900">{client.client_name || `${client.first_name} ${client.last_name}`.trim() || "—"}</h1>
                            <div className="flex flex-wrap items-center gap-2 mt-2">
                                {client.acos_id != null && (
                                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-primary/30 bg-primary/5" data-testid="header-acos-chip">
                                        <span className="field-label text-primary/80">A+ CaseFlow ID</span>
                                        <CopyableField value={client.acos_id} label="A+ CaseFlow ID" mono testId="header-acos-id" />
                                    </span>
                                )}
                                {client.case_id && (
                                    <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-border bg-gray-50" data-testid="header-case-id-chip">
                                        <span className="field-label">Skywave Case ID</span>
                                        <CopyableField value={client.case_id} label="Skywave Case ID" mono testId="header-case-id" />
                                    </span>
                                )}
                                {/* Payment Status — moved here from the contact line so it
                                    sits with the identity chips right under the client name.
                                    Read-only; edit lives in the Payments tab. */}
                                <PaymentStatusBadge value={client.payment_status || "active"} />
                                {stats?.active_alerts_count > 0 && (
                                    <span className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-md bg-rose-100 text-rose-700 border border-rose-200" data-testid="header-active-alert-count">
                                        <Bell className="h-3 w-3" /> {stats.active_alerts_count} active alert{stats.active_alerts_count > 1 ? "s" : ""}
                                    </span>
                                )}
                            </div>
                        </div>

                        {/* Contact line — phone + email are click-to-copy */}
                        <div className="flex flex-wrap items-center gap-x-5 gap-y-1 text-sm text-gray-700">
                            {phone ? (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try { await navigator.clipboard.writeText(phone); toast.success("Phone copied"); }
                                        catch { toast.error("Couldn't copy phone — check browser permissions"); }
                                    }}
                                    className="inline-flex items-center gap-1.5 hover:text-primary cursor-pointer"
                                    title="Copy phone"
                                    data-testid="header-copy-phone"
                                >
                                    <Phone className="h-4 w-4 text-muted-foreground" />{phone}
                                </button>
                            ) : (
                                <span className="text-muted-foreground inline-flex items-center gap-1.5"><Phone className="h-4 w-4" />No phone</span>
                            )}
                            {client.email ? (
                                <button
                                    type="button"
                                    onClick={async () => {
                                        try { await navigator.clipboard.writeText(client.email); toast.success("Email copied"); }
                                        catch { toast.error("Couldn't copy email — check browser permissions"); }
                                    }}
                                    className="inline-flex items-center gap-1.5 hover:text-primary cursor-pointer"
                                    title="Copy email"
                                    data-testid="header-copy-email"
                                >
                                    <Mail className="h-4 w-4 text-muted-foreground" />{client.email}
                                </button>
                            ) : (
                                <span className="text-muted-foreground inline-flex items-center gap-1.5"><Mail className="h-4 w-4" />No email</span>
                            )}
                        </div>
                    </div>

                    {/* Right-side controls — Workflow Status + Case Team modules and Add Note.
                        Desktop: horizontal row. Mobile: stacked full-width with
                        Workflow first (per mobile spec). */}
                    <div
                        className="flex flex-col sm:flex-row sm:items-center gap-2 w-full sm:w-auto sm:self-start"
                        data-testid="header-action-buttons"
                    >
                        <div className="w-full sm:w-auto order-1 sm:order-2" data-testid="header-workflow-wrapper">
                            <WorkflowStatusSelect client={client} onChanged={load} />
                        </div>
                        <div className="w-full sm:w-auto order-2 sm:order-1">
                            <CaseTeamControl client={client} onSaved={load} />
                        </div>
                        <Button
                            size="sm"
                            onClick={() => { setEditingNote(null); setNoteForm({ note_text: "", category: "General", pinned: false }); setNoteMentions([]); setAddNoteOpen(true); }}
                            data-testid="add-note-button"
                            className="h-10 w-full sm:w-auto order-3"
                        >
                            <Plus className="h-4 w-4 mr-1.5" /> Add Note
                        </Button>
                    </div>
                </div>
            </div>

            {!client.processor_user_id && (
                <div className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-900 flex items-center gap-2" data-testid="client-processor-banner">
                    <span className="inline-flex items-center justify-center h-5 w-5 rounded-full bg-amber-200 text-amber-900 text-xs font-bold">!</span>
                    <span><strong>Needs Processor.</strong> Open <em>Case Team</em> in the header to assign one.</span>
                </div>
            )}

            {/* Active alerts banner — accent-stripe cards */}
            {activeAlerts && activeAlerts.length > 0 && (
                <div className="space-y-2" data-testid="active-alerts-banner">
                    {activeAlerts.map(a => (
                        <div key={a.id} className={`${SEV_CARD_CLS[a.severity] || "card-info"} px-4 py-3`}>
                            <div className="flex items-start justify-between gap-3">
                                <div className="flex items-start gap-2.5">
                                    <SeverityIcon severity={a.severity} />
                                    <div>
                                        <div className="flex items-center gap-2"><AlertSeverityPill severity={a.severity} /><span className="text-sm font-medium text-gray-900">{a.alert_title}</span></div>
                                        {a.alert_body && <div className="text-sm text-gray-700 mt-1">{a.alert_body}</div>}
                                    </div>
                                </div>
                                <Button size="sm" variant="ghost" onClick={() => resolveAlert(a)} data-testid={`resolve-alert-${a.id}`}>
                                    <Check className="h-4 w-4 mr-1" /> Resolve
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <Tabs value={tab} onValueChange={setTab}>
                {/* Mobile: replace the horizontal tabs row with a Section select.
                    Desktop/tablet (sm+): keep the existing TabsList unchanged. */}
                <div className="sm:hidden mb-2" data-testid="mobile-section-select-wrap">
                    <label className="text-[10px] uppercase tracking-wider font-medium text-muted-foreground block mb-1">
                        Section
                    </label>
                    <Select value={tab} onValueChange={setTab}>
                        <SelectTrigger className="h-10 w-full" data-testid="mobile-section-select">
                            <SelectValue placeholder="Choose section…" />
                        </SelectTrigger>
                        <SelectContent>
                            <SelectItem value="overview" data-testid="mobile-section-option-overview">Overview</SelectItem>
                            <SelectItem value="history" data-testid="mobile-section-option-history">History</SelectItem>
                            <SelectItem value="notes" data-testid="mobile-section-option-notes">Notes</SelectItem>
                            <SelectItem value="communication" data-testid="mobile-section-option-communication">Communication</SelectItem>
                            <SelectItem value="loans" data-testid="mobile-section-option-loans">Loans</SelectItem>
                            <SelectItem value="employment" data-testid="mobile-section-option-employment">Employment / Income</SelectItem>
                            <SelectItem value="documents" data-testid="mobile-section-option-documents">Documents</SelectItem>
                            <SelectItem value="payments" data-testid="mobile-section-option-payments">Payments</SelectItem>
                            <SelectItem value="alerts" data-testid="mobile-section-option-alerts">Alerts</SelectItem>
                        </SelectContent>
                    </Select>
                </div>
                <TabsList data-testid="client-tabs" className="hidden sm:inline-flex">
                    <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
                    <TabsTrigger value="history" data-testid="tab-history">History</TabsTrigger>
                    <TabsTrigger value="notes" data-testid="tab-notes">Notes</TabsTrigger>
                    <TabsTrigger value="communication" data-testid="tab-communication">Communication</TabsTrigger>
                    <TabsTrigger value="loans" data-testid="tab-loans">Loans</TabsTrigger>
                    <TabsTrigger value="employment" data-testid="tab-employment">Employment / Income</TabsTrigger>
                    <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
                    <TabsTrigger value="payments" data-testid="tab-payments">Payments</TabsTrigger>
                    <TabsTrigger value="alerts" data-testid="tab-alerts">Alerts</TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="pt-4 space-y-4">
                    {/* One Edit/Save/Cancel for the whole Overview tab */}
                    <div className="flex items-center justify-between">
                        <div>
                            <div className="text-sm font-semibold text-gray-900">Case File</div>
                            <div className="text-xs text-muted-foreground">All fields below save together when you click Save.</div>
                        </div>
                        {editOverview ? (
                            <div className="flex gap-2">
                                <Button size="sm" variant="outline" onClick={() => setEditOverview(false)} data-testid="overview-cancel"><X className="h-4 w-4 mr-1" />Cancel</Button>
                                <Button size="sm" onClick={saveOverview} data-testid="overview-save">Save</Button>
                            </div>
                        ) : (
                            <Button size="sm" variant="outline" onClick={startEditOverview} data-testid="overview-edit"><Edit3 className="h-4 w-4 mr-1" />Edit Overview</Button>
                        )}
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                        {/* A. Identity & Case Info */}
                        <SectionCard title="Identity & Case Info" testIdRoot="identity">
                            {editOverview ? (
                                <div className="grid grid-cols-2 gap-2.5">
                                    <Field label="A+ CaseFlow ID" full><div className="text-sm py-2 text-muted-foreground"><CopyableField value={client.acos_id} label="A+ CaseFlow ID" mono /> <span className="text-[10px]">(read-only)</span></div></Field>
                                    <Field label="Skywave Case ID" full><Input value={overviewDraft.case_id} onChange={e => setOverviewDraft({ ...overviewDraft, case_id: e.target.value })} data-testid="edit-case-id" /></Field>
                                    <Field label="First Name"><Input value={overviewDraft.first_name} onChange={e => setOverviewDraft({ ...overviewDraft, first_name: e.target.value })} data-testid="edit-first-name" /></Field>
                                    <Field label="Last Name"><Input value={overviewDraft.last_name} onChange={e => setOverviewDraft({ ...overviewDraft, last_name: e.target.value })} data-testid="edit-last-name" /></Field>
                                    <Field label="DOB"><Input type="date" value={overviewDraft.dob} onChange={e => setOverviewDraft({ ...overviewDraft, dob: e.target.value })} data-testid="edit-dob" /></Field>
                                    <Field label="SSN (Last 4)"><Input maxLength={11} value={overviewDraft.ssn_last4} onChange={e => setOverviewDraft({ ...overviewDraft, ssn_last4: e.target.value })} placeholder="Last 4 only" data-testid="edit-ssn4" /></Field>
                                    <Field label="Status">
                                        <Select value={overviewDraft.client_status} onValueChange={v => setOverviewDraft({ ...overviewDraft, client_status: v })}>
                                            <SelectTrigger data-testid="edit-client-status"><SelectValue /></SelectTrigger>
                                            <SelectContent>{STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace("_", " ")}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Program">
                                        <Select value={overviewDraft.program_type} onValueChange={v => setOverviewDraft({ ...overviewDraft, program_type: v })}>
                                            <SelectTrigger data-testid="edit-program-type"><SelectValue /></SelectTrigger>
                                            <SelectContent>{PROGRAMS.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Assigned Specialist" full><Input value={overviewDraft.assigned_rep} onChange={e => setOverviewDraft({ ...overviewDraft, assigned_rep: e.target.value })} data-testid="edit-assigned-rep" /></Field>
                                    <Field label="Processor (required)" full>
                                        <UserAssignSelect
                                            value={overviewDraft.processor_user_id}
                                            onSelect={(u) => setOverviewDraft({
                                                ...overviewDraft,
                                                processor_user_id: u?.id || null,
                                                processor_name: u?.name || "",
                                                processor_email: u?.email || "",
                                            })}
                                            placeholder="Assign processor…"
                                            testId="edit-processor"
                                            triggerClassName="h-9 text-xs w-full"
                                        />
                                    </Field>
                                    <Field label="Sales Rep" full>
                                        <UserAssignSelect
                                            value={overviewDraft.sales_rep_user_id}
                                            onSelect={(u) => setOverviewDraft({
                                                ...overviewDraft,
                                                sales_rep_user_id: u?.id || null,
                                                sales_rep_name: u?.name || "",
                                                sales_rep_email: u?.email || "",
                                            })}
                                            placeholder="Assign sales rep…"
                                            testId="edit-sales-rep"
                                            triggerClassName="h-9 text-xs w-full"
                                        />
                                    </Field>
                                </div>
                            ) : (
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                                    <ROCell label="A+ CaseFlow ID"><CopyableField value={client.acos_id} label="A+ CaseFlow ID" mono /></ROCell>
                                    <ROCell label="Skywave Case ID">{client.case_id ? <CopyableField value={client.case_id} label="Case ID" mono /> : "—"}</ROCell>
                                    <ROCell label="First Name">{client.first_name || "—"}</ROCell>
                                    <ROCell label="Last Name">{client.last_name || "—"}</ROCell>
                                    <ROCell label="DOB">{client.dob || "—"}</ROCell>
                                    <ROCell label="SSN (Last 4)">{client.ssn_last4 ? `••• ${client.ssn_last4}` : "—"}</ROCell>
                                    <ROCell label="Status"><span className="capitalize">{(client.client_status || "active").replace("_", " ")}</span></ROCell>
                                    <ROCell label="Program">{client.program_type || "—"}</ROCell>
                                    <ROCell label="Assigned Specialist">{client.assigned_rep || "—"}</ROCell>
                                    <ROCell label="Processor">{client.processor_name ? <span data-testid="client-processor-name">{client.processor_name}{client.processor_email ? <span className="text-xs text-muted-foreground"> — {client.processor_email}</span> : null}</span> : <span className="text-amber-700 font-medium" data-testid="client-needs-processor">Needs Processor</span>}</ROCell>
                                    <ROCell label="Sales Rep">{client.sales_rep_name ? <span data-testid="client-sales-rep-name">{client.sales_rep_name}{client.sales_rep_email ? <span className="text-xs text-muted-foreground"> — {client.sales_rep_email}</span> : null}</span> : "—"}</ROCell>
                                    <ROCell label="Source Lead">{client.source_lead_id ? <Link to={`/leads/${client.source_lead_id}`} className="text-primary hover:underline font-mono text-xs">{client.source_lead_id.slice(0, 8)}…</Link> : "—"}</ROCell>
                                    <ROCell label="Created">{client.created_at ? new Date(client.created_at).toLocaleDateString() : "—"}</ROCell>
                                </dl>
                            )}
                        </SectionCard>

                        {/* B. Contact Info */}
                        <SectionCard title="Contact Info" testIdRoot="contact">
                            {editOverview ? (
                                <div className="grid grid-cols-2 gap-2.5">
                                    <Field label="Primary Phone"><Input value={overviewDraft.phone_raw} onChange={e => setOverviewDraft({ ...overviewDraft, phone_raw: e.target.value })} data-testid="edit-phone-raw" /></Field>
                                    <Field label="Alternate Phone"><Input value={overviewDraft.alternate_phone} onChange={e => setOverviewDraft({ ...overviewDraft, alternate_phone: e.target.value })} data-testid="edit-alt-phone" /></Field>
                                    <Field label="Email"><Input type="email" value={overviewDraft.email} onChange={e => setOverviewDraft({ ...overviewDraft, email: e.target.value })} data-testid="edit-email" /></Field>
                                    <Field label="Alternate Email"><Input type="email" value={overviewDraft.alternate_email} onChange={e => setOverviewDraft({ ...overviewDraft, alternate_email: e.target.value })} data-testid="edit-alt-email" /></Field>
                                    <Field label="Preferred Contact Method">
                                        <Select value={overviewDraft.preferred_contact_method || "none"} onValueChange={v => setOverviewDraft({ ...overviewDraft, preferred_contact_method: v === "none" ? "" : v })}>
                                            <SelectTrigger data-testid="edit-preferred-contact"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                <SelectItem value="none">—</SelectItem>
                                                <SelectItem value="sms">SMS</SelectItem>
                                                <SelectItem value="email">Email</SelectItem>
                                                <SelectItem value="phone">Phone</SelectItem>
                                                <SelectItem value="mail">Mail</SelectItem>
                                            </SelectContent>
                                        </Select>
                                    </Field>
                                    <Field label="Best Time to Contact"><Input value={overviewDraft.best_time_to_contact} onChange={e => setOverviewDraft({ ...overviewDraft, best_time_to_contact: e.target.value })} placeholder="e.g. Weekdays 9-5 PT" /></Field>
                                    <Field label="Time Zone"><Input value={overviewDraft.timezone} onChange={e => setOverviewDraft({ ...overviewDraft, timezone: e.target.value })} placeholder="e.g. America/Los_Angeles" /></Field>
                                    <Field label="Language"><Input value={overviewDraft.language} onChange={e => setOverviewDraft({ ...overviewDraft, language: e.target.value })} placeholder="e.g. English" /></Field>
                                </div>
                            ) : (
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                                    <ROCell label="Phone (raw)" mono>{client.phone_raw ? <a href={`tel:${client.phone_raw}`} className="hover:text-primary">{client.phone_raw}</a> : "—"}</ROCell>
                                    <ROCell label="Phone (E.164)" mono>{client.phone_e164 ? <a href={`tel:${client.phone_e164}`} className="hover:text-primary">{client.phone_e164}</a> : "—"}</ROCell>
                                    <ROCell label="Alternate Phone" mono>{client.alternate_phone ? <a href={`tel:${client.alternate_phone}`} className="hover:text-primary">{client.alternate_phone}</a> : "—"}</ROCell>
                                    <ROCell label="Email">{client.email ? <a href={`mailto:${client.email}`} className="text-primary hover:underline">{client.email}</a> : "—"}</ROCell>
                                    <ROCell label="Alternate Email">{client.alternate_email ? <a href={`mailto:${client.alternate_email}`} className="text-primary hover:underline">{client.alternate_email}</a> : "—"}</ROCell>
                                    <ROCell label="Preferred Contact">{client.preferred_contact_method || "—"}</ROCell>
                                    <ROCell label="Best Time">{client.best_time_to_contact || "—"}</ROCell>
                                    <ROCell label="Time Zone">{client.timezone || "—"}</ROCell>
                                    <ROCell label="Language">{client.language || "—"}</ROCell>
                                </dl>
                            )}
                        </SectionCard>

                        {/* C. Address */}
                        <SectionCard title="Address" testIdRoot="address">
                            {editOverview ? (
                                <div className="grid grid-cols-2 gap-2.5">
                                    <Field label="Address Line 1" full><Input value={overviewDraft.address_line_1} onChange={e => setOverviewDraft({ ...overviewDraft, address_line_1: e.target.value })} data-testid="edit-address-1" /></Field>
                                    <Field label="Address Line 2" full><Input value={overviewDraft.address_line_2} onChange={e => setOverviewDraft({ ...overviewDraft, address_line_2: e.target.value })} data-testid="edit-address-2" /></Field>
                                    <Field label="City"><Input value={overviewDraft.city} onChange={e => setOverviewDraft({ ...overviewDraft, city: e.target.value })} data-testid="edit-city" /></Field>
                                    <Field label="State"><Input value={overviewDraft.state} onChange={e => setOverviewDraft({ ...overviewDraft, state: e.target.value })} data-testid="edit-state" /></Field>
                                    <Field label="Zip"><Input value={overviewDraft.zip} onChange={e => setOverviewDraft({ ...overviewDraft, zip: e.target.value })} data-testid="edit-zip" /></Field>
                                    <Field label="County"><Input value={overviewDraft.county} onChange={e => setOverviewDraft({ ...overviewDraft, county: e.target.value })} /></Field>
                                </div>
                            ) : (
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                                    <ROCell label="Address 1" full>
                                        {(client.address_line_1 || client.address) ? (
                                            <a href={`https://maps.google.com/?q=${encodeURIComponent([client.address_line_1 || client.address, client.address_line_2, client.city, client.state, client.zip].filter(Boolean).join(", "))}`} target="_blank" rel="noreferrer" className="text-primary hover:underline">
                                                {client.address_line_1 || client.address}
                                            </a>
                                        ) : "—"}
                                    </ROCell>
                                    <ROCell label="Address 2" full>{client.address_line_2 || "—"}</ROCell>
                                    <ROCell label="City">{client.city || "—"}</ROCell>
                                    <ROCell label="State">{client.state || "—"}</ROCell>
                                    <ROCell label="Zip">{client.zip || "—"}</ROCell>
                                    <ROCell label="County">{client.county || "—"}</ROCell>
                                </dl>
                            )}
                        </SectionCard>

                        {/* D. Case Management */}
                        <SectionCard title="Case Management" testIdRoot="case-mgmt">
                            {editOverview ? (
                                <div className="grid grid-cols-2 gap-2.5">
                                    <Field label="Service Type"><Input value={overviewDraft.service_type} onChange={e => setOverviewDraft({ ...overviewDraft, service_type: e.target.value })} placeholder="e.g. consolidation" /></Field>
                                    <Field label="Enrolled Date"><Input type="date" value={overviewDraft.enrolled_date} onChange={e => setOverviewDraft({ ...overviewDraft, enrolled_date: e.target.value })} /></Field>
                                    <Field label="Program Start"><Input type="date" value={overviewDraft.program_start_date} onChange={e => setOverviewDraft({ ...overviewDraft, program_start_date: e.target.value })} /></Field>
                                    <Field label="Recertification Due"><Input type="date" value={overviewDraft.recertification_due_date} onChange={e => setOverviewDraft({ ...overviewDraft, recertification_due_date: e.target.value })} data-testid="edit-recert-due" /></Field>
                                    <Field label="Renewal Due"><Input type="date" value={overviewDraft.renewal_due_date} onChange={e => setOverviewDraft({ ...overviewDraft, renewal_due_date: e.target.value })} /></Field>
                                    <Field label="Important Deadline"><Input type="date" value={overviewDraft.important_deadline} onChange={e => setOverviewDraft({ ...overviewDraft, important_deadline: e.target.value })} /></Field>
                                    <Field label="Internal Warning Notes" full><Textarea rows={2} value={overviewDraft.internal_warning_notes} onChange={e => setOverviewDraft({ ...overviewDraft, internal_warning_notes: e.target.value })} data-testid="edit-warning-notes" /></Field>
                                    <Field label="Preferred Contact Notes" full><Textarea rows={2} value={overviewDraft.preferred_contact_notes} onChange={e => setOverviewDraft({ ...overviewDraft, preferred_contact_notes: e.target.value })} /></Field>
                                </div>
                            ) : (
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                                    <ROCell label="Service Type">{client.service_type || "—"}</ROCell>
                                    <ROCell label="Enrolled">{client.enrolled_date || "—"}</ROCell>
                                    <ROCell label="Program Start">{client.program_start_date || "—"}</ROCell>
                                    <ROCell label="Recertification Due">{client.recertification_due_date || "—"}</ROCell>
                                    <ROCell label="Renewal Due">{client.renewal_due_date || "—"}</ROCell>
                                    <ROCell label="Important Deadline">{client.important_deadline || "—"}</ROCell>
                                    <ROCell label="Last Contacted">{client.last_contacted_at ? new Date(client.last_contacted_at).toLocaleString() : "—"}</ROCell>
                                    <ROCell label="Internal Warning" full>{client.internal_warning_notes || <span className="text-muted-foreground italic">—</span>}</ROCell>
                                    <ROCell label="Preferred Contact Notes" full>{client.preferred_contact_notes || <span className="text-muted-foreground italic">—</span>}</ROCell>
                                </dl>
                            )}
                        </SectionCard>

                        {/* E. Contact Restrictions / Flags */}
                        <SectionCard title="Contact Restrictions" testIdRoot="restrictions">
                            {editOverview ? (
                                <div className="grid grid-cols-2 gap-2.5">
                                    <RestrictionToggle label="Do Not Call" testId="edit-dnc" checked={overviewDraft.do_not_call} onChange={v => setOverviewDraft({ ...overviewDraft, do_not_call: v })} />
                                    <RestrictionToggle label="Do Not Text" testId="edit-dnt" checked={overviewDraft.do_not_text} onChange={v => setOverviewDraft({ ...overviewDraft, do_not_text: v })} />
                                    <RestrictionToggle label="Do Not Email" testId="edit-dne" checked={overviewDraft.do_not_email} onChange={v => setOverviewDraft({ ...overviewDraft, do_not_email: v })} />
                                    <RestrictionToggle label="Do Not Mail" testId="edit-dnm" checked={overviewDraft.do_not_mail} onChange={v => setOverviewDraft({ ...overviewDraft, do_not_mail: v })} />
                                    <Field label="Compliance Notes" full><Textarea rows={2} value={overviewDraft.compliance_notes} onChange={e => setOverviewDraft({ ...overviewDraft, compliance_notes: e.target.value })} /></Field>
                                </div>
                            ) : (
                                <RestrictionsView c={client} />
                            )}
                        </SectionCard>

                        {/* F. Activity Snapshot (read-only) */}
                        <SectionCard title="Activity Snapshot" testIdRoot="activity">
                            <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm" data-testid="activity-snapshot">
                                <ROCell label="Total Communications">{String(stats?.communications_count ?? 0)}</ROCell>
                                <ROCell label="Total Notes">{String(stats?.notes_count ?? 0)}</ROCell>
                                <ROCell label="Active Alerts">{String(stats?.active_alerts_count ?? 0)}</ROCell>
                                <ROCell label="Documents Received">{String(stats?.documents_received_count ?? 0)}</ROCell>
                                <ROCell label="Documents Needed">{String(stats?.documents_needed_count ?? 0)}</ROCell>
                                <ROCell label="Included Loan Balance">{loanSnap?.included_loan_balance != null ? `$${Number(loanSnap.included_loan_balance).toLocaleString(undefined, { maximumFractionDigits: 2 })}` : "—"}</ROCell>
                                <ROCell label="Selected Program">{loanSnap?.selected_plan || "—"}</ROCell>
                                <ROCell label="Estimated Payment">{loanSnap?.estimated_payment != null ? `$${Number(loanSnap.estimated_payment).toLocaleString()}/mo` : "—"}</ROCell>
                            </dl>
                        </SectionCard>

                        {/* Employment / Income snapshot */}
                        <SectionCard title="Employment / Income" testIdRoot="employment-snap">
                            {empSnap ? (
                                <dl className="grid grid-cols-2 gap-x-4 gap-y-1.5 text-sm">
                                    <ROCell label="Status">{(empSnap.employment_status || "—").replace("_", " ")}</ROCell>
                                    <ROCell label="Employer">{empSnap.employer_name || "—"}</ROCell>
                                    <ROCell label="Annual Income">{empSnap.annual_income != null ? `$${Number(empSnap.annual_income).toLocaleString()}` : "—"}</ROCell>
                                    <ROCell label="Last Update">{empSnap.last_income_update ? new Date(empSnap.last_income_update).toLocaleDateString() : "—"}</ROCell>
                                </dl>
                            ) : (
                                <div className="text-xs text-muted-foreground py-2">No employment data yet. Open the Employment / Income tab to add it.</div>
                            )}
                        </SectionCard>
                    </div>
                </TabsContent>

                <TabsContent value="history" className="pt-4 space-y-4">
                    <div className="bg-white border border-border rounded-xl p-5 shadow-sm">
                        {history.length === 0 ? (
                            <EmptyState title="No history yet." help="Events like status changes, notes, and alerts will appear here." />
                        ) : (
                            <ol className="relative border-l border-border ml-2">
                                {history.map(h => (
                                    <li key={h.id} className="ml-4 pb-4">
                                        <span className="absolute -left-[5px] mt-1.5 h-2 w-2 rounded-full bg-primary" />
                                        <div className="text-sm font-medium text-gray-900">{h.event_title}</div>
                                        {h.event_body && <div className="text-sm text-gray-600 mt-0.5">{h.event_body}</div>}
                                        <div className="text-xs text-muted-foreground mt-0.5">{new Date(h.created_at).toLocaleString()} · {h.created_by_name || "system"}</div>
                                    </li>
                                ))}
                            </ol>
                        )}
                    </div>
                    <ClientActionRunsPanel clientId={id} />
                </TabsContent>

                <TabsContent value="notes" className="pt-4">
                    {/* Phase 2 — prominent compose-at-top */}
                    <div className="bg-white border border-border rounded-xl p-5 shadow-sm mb-4">
                        <div className="text-sm font-medium text-gray-900 mb-2">Add a note</div>
                        <MentionTextarea
                            value={inlineNote.note_text}
                            onChange={(v) => setInlineNote({ ...inlineNote, note_text: v })}
                            onMentionPick={(u) => setInlineNoteMentions(prev => [...prev, u])}
                            candidates={mentionCandidates}
                            rows={4}
                            placeholder="What happened on this client? Type @ to mention Processor / Sales Rep."
                            testId="inline-note-text"
                        />
                        <div className="flex flex-wrap items-end gap-3 mt-3">
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-gray-700">Category</label>
                                <Select value={inlineNote.category} onValueChange={v => setInlineNote({ ...inlineNote, category: v })}>
                                    <SelectTrigger className="w-[180px]" data-testid="inline-note-category"><SelectValue /></SelectTrigger>
                                    <SelectContent>{NOTE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <label className="flex items-center gap-2 text-sm pb-2">
                                <input type="checkbox" checked={inlineNote.pinned} onChange={e => setInlineNote({ ...inlineNote, pinned: e.target.checked })} data-testid="inline-note-pinned" />
                                <span>Pin to top</span>
                            </label>
                            <div className="flex-1" />
                            <Button
                                onClick={async () => {
                                    if (!inlineNote.note_text.trim()) return;
                                    setInlineBusy(true);
                                    try {
                                        const ids = Array.from(new Set(inlineNoteMentions.map(u => u.id)));
                                        await api.post(`/clients/${id}/notes`, { ...inlineNote, mentioned_user_ids: ids });
                                        toast.success("Note added");
                                        setInlineNote({ note_text: "", category: "General", pinned: false });
                                        setInlineNoteMentions([]);
                                        // F4.2: nudge the bell right away
                                        window.dispatchEvent(new Event("notifications:refresh"));
                                        loadNotes(); loadHistory(); load();
                                    } catch (e) { toast.error(formatApiError(e)); }
                                    finally { setInlineBusy(false); }
                                }}
                                disabled={!inlineNote.note_text.trim() || inlineBusy}
                                data-testid="inline-note-save"
                            >
                                {inlineBusy ? "Saving…" : "Save Note"}
                            </Button>
                        </div>
                    </div>

                    {/* Existing notes list */}
                    <div className="bg-white border border-border rounded-xl p-5 shadow-sm">
                        {notes.length === 0 ? (
                            <EmptyState title="No notes yet." help="Use the composer above to capture key context. Pin important notes to the top." />
                        ) : (
                            <ul className="divide-y divide-border">
                                {notes.map(n => (
                                    <li key={n.id} className="py-3 flex items-start gap-3" data-testid={`note-row-${n.id}`}>
                                        <button onClick={() => togglePin(n)} className="mt-1 text-muted-foreground hover:text-amber-600" title={n.pinned ? "Unpin" : "Pin"}>
                                            {n.pinned ? <Pin className="h-4 w-4 text-amber-600" /> : <PinOff className="h-4 w-4" />}
                                        </button>
                                        <div className="flex-1">
                                            <div className="text-sm text-gray-900 whitespace-pre-wrap">{n.note_text}</div>
                                            <div className="text-xs text-muted-foreground mt-1">
                                                {n.category && <span className="px-1.5 py-0.5 rounded bg-gray-100 mr-2">{n.category}</span>}
                                                {n.created_by_name || "—"} · {new Date(n.created_at).toLocaleString()}
                                                {n.updated_at && ` · edited ${new Date(n.updated_at).toLocaleString()}`}
                                            </div>
                                        </div>
                                        <Button size="sm" variant="ghost" onClick={() => { setEditingNote(n); setNoteForm({ note_text: n.note_text, category: n.category || "General", pinned: !!n.pinned }); setAddNoteOpen(true); }}>Edit</Button>
                                        <Button size="sm" variant="ghost" onClick={() => deleteNote(n)} className="text-rose-600"><Trash2 className="h-4 w-4" /></Button>
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </TabsContent>

                <TabsContent value="communication" className="pt-4">
                    <ClientCommunicationTab client={client} onCommSent={() => load()} />
                </TabsContent>

                <TabsContent value="loans" className="pt-4">
                    <ClientLoansTab client={client} onChanged={() => { load(); loadHistory(); loadLoanSnap(); }} />
                </TabsContent>
                <TabsContent value="employment" className="pt-4">
                    <ClientEmploymentTab client={client} onSaved={() => load()} />
                </TabsContent>
                <TabsContent value="documents" className="pt-4">
                    <ClientDocumentsTab client={client} onChanged={() => load()} />
                </TabsContent>

                <TabsContent value="payments" className="pt-4">
                    <div className="bg-white border border-border rounded-xl p-5 shadow-sm space-y-4" data-testid="payments-tab-panel">
                        <div className="flex items-center gap-2">
                            <CreditCard className="h-4 w-4 text-emerald-700" />
                            <h3 className="text-sm font-semibold text-gray-900">Current Payment Status</h3>
                        </div>
                        <div className="grid gap-3 sm:grid-cols-2 max-w-md">
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-gray-700">Payment status</label>
                                <Select value={client.payment_status || "active"} onValueChange={updatePaymentStatus}>
                                    <SelectTrigger data-testid="payments-tab-payment-status-select">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {PAYMENT_STATUSES.map(p => (
                                            <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div className="flex items-end">
                                <PaymentStatusBadge value={client.payment_status || "active"} />
                            </div>
                        </div>
                        <p className="text-xs text-muted-foreground border-t border-dashed pt-3" data-testid="payments-tab-scaffold-note">
                            Merchant payment sync coming soon.
                        </p>
                    </div>
                </TabsContent>

                <TabsContent value="alerts" className="pt-4">
                    <div className="bg-white border border-border rounded-xl p-5 shadow-sm">
                        <div className="flex items-center justify-between mb-3">
                            <h3 className="text-sm font-semibold text-gray-900">Alerts</h3>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => { setEditingAlert(null); setAlertForm({ alert_title: "", alert_body: "", severity: "warning" }); setAddAlertOpen(true); }}
                                data-testid="alerts-tab-add-alert-button"
                            >
                                <Bell className="h-4 w-4 mr-1.5" /> Add Alert
                            </Button>
                        </div>
                        {alerts.length === 0 ? (
                            <EmptyState title="No alerts yet." help="Use the Add Alert button above to flag urgent issues that need follow-up." />
                        ) : (
                            <ul className="divide-y divide-border">
                                {alerts.map(a => (
                                    <li key={a.id} className="py-3 flex items-start gap-3" data-testid={`alert-row-${a.id}`}>
                                        <SeverityIcon severity={a.severity} />
                                        <div className="flex-1">
                                            <div className="flex items-center gap-2">
                                                <AlertSeverityPill severity={a.severity} />
                                                <span className="text-sm font-medium text-gray-900">{a.alert_title}</span>
                                                {!a.active && <span className="text-[10px] uppercase tracking-wider font-medium px-1.5 py-0.5 rounded border bg-gray-100 text-gray-500">resolved</span>}
                                            </div>
                                            {a.alert_body && <div className="text-sm text-gray-600 mt-1">{a.alert_body}</div>}
                                            <div className="text-xs text-muted-foreground mt-1">{a.created_by_name || "—"} · {new Date(a.created_at).toLocaleString()}{a.resolved_at && ` · resolved ${new Date(a.resolved_at).toLocaleDateString()}`}</div>
                                        </div>
                                        {a.active && (
                                            <>
                                                <Button size="sm" variant="ghost" onClick={() => { setEditingAlert(a); setAlertForm({ alert_title: a.alert_title, alert_body: a.alert_body || "", severity: a.severity }); setAddAlertOpen(true); }}>Edit</Button>
                                                <Button size="sm" variant="ghost" onClick={() => resolveAlert(a)}><Check className="h-4 w-4 mr-1" />Resolve</Button>
                                            </>
                                        )}
                                    </li>
                                ))}
                            </ul>
                        )}
                    </div>
                </TabsContent>
            </Tabs>

            {/* Add/edit note */}
            <Dialog open={addNoteOpen} onOpenChange={(v) => { setAddNoteOpen(v); if (!v) setEditingNote(null); }}>
                <DialogContent className="sm:max-w-[460px]" data-testid="note-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingNote ? "Edit Note" : "Add Note"}</DialogTitle>
                        <DialogDescription>Notes are saved to this client and surface in the History tab.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <MentionTextarea value={noteForm.note_text} onChange={(v) => setNoteForm({ ...noteForm, note_text: v })} onMentionPick={(u) => setNoteMentions(prev => [...prev, u])} candidates={mentionCandidates} rows={5} placeholder="Note text… Type @ to mention Processor / Sales Rep." testId="note-text-input" />
                        <div className="grid grid-cols-2 gap-3">
                            <div className="space-y-1.5">
                                <label className="text-xs font-medium text-gray-700">Category</label>
                                <Select value={noteForm.category} onValueChange={v => setNoteForm({ ...noteForm, category: v })}>
                                    <SelectTrigger data-testid="note-category-select"><SelectValue /></SelectTrigger>
                                    <SelectContent>{NOTE_CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                                </Select>
                            </div>
                            <label className="flex items-end gap-2 text-sm pb-1">
                                <input type="checkbox" checked={noteForm.pinned} onChange={e => setNoteForm({ ...noteForm, pinned: e.target.checked })} data-testid="note-pinned-checkbox" />
                                <span>Pin to top</span>
                            </label>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddNoteOpen(false)}>Cancel</Button>
                        <Button onClick={submitNote} disabled={!noteForm.note_text.trim()} data-testid="note-submit">{editingNote ? "Save" : "Add"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add/edit alert */}
            <Dialog open={addAlertOpen} onOpenChange={(v) => { setAddAlertOpen(v); if (!v) setEditingAlert(null); }}>
                <DialogContent className="sm:max-w-[460px]" data-testid="alert-dialog">
                    <DialogHeader>
                        <DialogTitle>{editingAlert ? "Edit Alert" : "Add Alert"}</DialogTitle>
                        <DialogDescription>Active alerts appear at the top of this client's page until resolved.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700">Title</label>
                            <Input value={alertForm.alert_title} onChange={e => setAlertForm({ ...alertForm, alert_title: e.target.value })} data-testid="alert-title-input" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700">Body (optional)</label>
                            <Textarea value={alertForm.alert_body} onChange={e => setAlertForm({ ...alertForm, alert_body: e.target.value })} rows={3} data-testid="alert-body-input" />
                        </div>
                        <div className="space-y-1.5">
                            <label className="text-xs font-medium text-gray-700">Severity</label>
                            <Select value={alertForm.severity} onValueChange={v => setAlertForm({ ...alertForm, severity: v })}>
                                <SelectTrigger data-testid="alert-severity-select"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="info">Info</SelectItem>
                                    <SelectItem value="warning">Warning</SelectItem>
                                    <SelectItem value="urgent">Urgent</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setAddAlertOpen(false)}>Cancel</Button>
                        <Button onClick={submitAlert} disabled={!alertForm.alert_title.trim()} data-testid="alert-submit">{editingAlert ? "Save" : "Add"}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}

function SectionCard({ title, editing, onEdit, onCancel, onSave, testIdRoot, children }) {
    return (
        <div className="bg-white border border-border rounded-xl p-5 shadow-sm">
            <div className="flex items-center justify-between mb-3">
                <div className="text-sm font-medium text-gray-900">{title}</div>
                {onEdit && (editing ? (
                    <div className="flex gap-1.5">
                        <Button size="sm" variant="outline" onClick={onCancel} data-testid={testIdRoot ? `${testIdRoot}-cancel` : undefined}><X className="h-3.5 w-3.5 mr-1" />Cancel</Button>
                        <Button size="sm" onClick={onSave} data-testid={testIdRoot ? `${testIdRoot}-save` : undefined}>Save</Button>
                    </div>
                ) : (
                    <Button size="sm" variant="ghost" onClick={onEdit} data-testid={testIdRoot ? `${testIdRoot}-edit` : undefined}><Edit3 className="h-3.5 w-3.5 mr-1" />Edit</Button>
                ))}
            </div>
            {children}
        </div>
    );
}

function Row({ label, children, mono = false }) {
    return (
        <div className="flex items-start justify-between gap-3">
            <dt className="text-xs uppercase tracking-wider text-muted-foreground pt-0.5">{label}</dt>
            <dd className={`text-right text-gray-800 ${mono ? "font-mono text-xs" : ""}`}>{children || "—"}</dd>
        </div>
    );
}

// Read-only flow-friendly grid cell — label on top, value below. Used in
// the rewritten Overview tab so first/last name / phone/email / address
// flow naturally left-to-right instead of the old right-aligned `<Row/>`.
function ROCell({ label, children, full = false, mono = false }) {
    return (
        <div className={full ? "col-span-2" : ""}>
            <div className="text-[10px] uppercase tracking-wider text-muted-foreground font-medium">{label}</div>
            <div className={`text-sm text-gray-900 ${mono ? "font-mono text-xs" : ""}`}>{children || <span className="text-muted-foreground">—</span>}</div>
        </div>
    );
}

function RestrictionToggle({ label, checked, onChange, testId }) {
    return (
        <label className="flex items-center gap-2 text-sm py-1 cursor-pointer select-none" data-testid={testId}>
            <input type="checkbox" checked={!!checked} onChange={e => onChange(e.target.checked)} className="h-4 w-4 accent-rose-600" />
            <span>{label}</span>
        </label>
    );
}

function RestrictionsView({ c }) {
    const flags = [
        c.do_not_call && "Do Not Call",
        c.do_not_text && "Do Not Text",
        c.do_not_email && "Do Not Email",
        c.do_not_mail && "Do Not Mail",
    ].filter(Boolean);
    if (!flags.length && !c.compliance_notes) {
        return <div className="text-xs text-muted-foreground italic" data-testid="no-restrictions">No contact restrictions.</div>;
    }
    return (
        <div className="space-y-2" data-testid="restrictions-view">
            {flags.length > 0 && (
                <div className="flex flex-wrap gap-1.5">
                    {flags.map(f => (
                        <span key={f} className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-semibold px-1.5 py-0.5 rounded bg-rose-100 text-rose-700 border border-rose-200" data-testid={`restriction-${f.toLowerCase().replace(/ /g, "-")}`}>
                            {f}
                        </span>
                    ))}
                </div>
            )}
            {c.compliance_notes && <div className="text-xs text-gray-700">{c.compliance_notes}</div>}
        </div>
    );
}

function Field({ label, children, full = false }) {
    return (
        <label className={`block space-y-1 ${full ? "col-span-2" : ""}`}>
            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
            {children}
        </label>
    );
}

function EmptyState({ title, help }) {
    return (
        <div className="text-center py-8 text-muted-foreground">
            <div className="text-sm font-medium text-gray-700">{title}</div>
            <div className="text-xs mt-1">{help}</div>
        </div>
    );
}

/* ── Master Workflow status pill + selector (W1) ───────────────────────── */
const WORKFLOW_COLOR_CLASS = {
    blue: "bg-blue-100 text-blue-800 border-blue-300",
    purple: "bg-purple-100 text-purple-800 border-purple-300",
    indigo: "bg-indigo-100 text-indigo-800 border-indigo-300",
    amber: "bg-amber-100 text-amber-800 border-amber-300",
    orange: "bg-orange-100 text-orange-800 border-orange-300",
    teal: "bg-teal-100 text-teal-800 border-teal-300",
    slate: "bg-slate-100 text-slate-800 border-slate-300",
    navy: "bg-blue-200 text-blue-900 border-blue-400",
    green: "bg-emerald-100 text-emerald-800 border-emerald-300",
    cyan: "bg-cyan-100 text-cyan-800 border-cyan-300",
    rose: "bg-rose-100 text-rose-800 border-rose-300",
};

// Bolder tint used ONLY by the Workflow Status header module so the
// whole module clearly reflects the current status. Keeps the pale
// badge tokens (WORKFLOW_COLOR_CLASS) untouched for other callers.
const WORKFLOW_MODULE_TINT = {
    blue:   { frame: "bg-blue-50    border-blue-400   text-blue-900",   pill: "bg-blue-500   text-white" },
    purple: { frame: "bg-purple-50  border-purple-400 text-purple-900", pill: "bg-purple-500 text-white" },
    indigo: { frame: "bg-indigo-50  border-indigo-400 text-indigo-900", pill: "bg-indigo-500 text-white" },
    amber:  { frame: "bg-amber-50   border-amber-400  text-amber-900",  pill: "bg-amber-500  text-white" },
    orange: { frame: "bg-orange-50  border-orange-400 text-orange-900", pill: "bg-orange-500 text-white" },
    teal:   { frame: "bg-teal-50    border-teal-400   text-teal-900",   pill: "bg-teal-500   text-white" },
    slate:  { frame: "bg-slate-50   border-slate-400  text-slate-900",  pill: "bg-slate-500  text-white" },
    navy:   { frame: "bg-blue-100   border-blue-500   text-blue-900",   pill: "bg-blue-700   text-white" },
    green:  { frame: "bg-emerald-50 border-emerald-400 text-emerald-900", pill: "bg-emerald-500 text-white" },
    cyan:   { frame: "bg-cyan-50    border-cyan-400   text-cyan-900",   pill: "bg-cyan-500   text-white" },
    rose:   { frame: "bg-rose-50    border-rose-400   text-rose-900",   pill: "bg-rose-500   text-white" },
};

function WorkflowStatusBadge({ name, color }) {
    const cls = WORKFLOW_COLOR_CLASS[color] || WORKFLOW_COLOR_CLASS.slate;
    return (
        <span className={`inline-flex items-center text-[10.5px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded border ${cls}`}
              data-testid="header-workflow-status-pill">
            {name}
        </span>
    );
}

// ── Workflow Status — module-style Popover matching the Case Team control.
// Color-tinted by the current status (reuses WORKFLOW_COLOR_CLASS).
// Clicking opens a scrollable list of workflow statuses; selecting a
// different status POSTs /api/clients/{id}/workflow-status which
// triggers the existing W2B/W2C/W2D automation pipeline (unchanged).
function WorkflowStatusSelect({ client, onChanged }) {
    const [statuses, setStatuses] = useState([]);
    const [busy, setBusy] = useState(false);
    const [open, setOpen] = useState(false);
    useEffect(() => {
        api.get("/workflow/statuses?include_inactive=false")
            .then(r => setStatuses((r.data || []).sort((a, b) => a.order - b.order)))
            .catch(() => {});
    }, []);
    const change = async (statusId) => {
        if (statusId === client.workflow_status_id) { setOpen(false); return; }
        setBusy(true);
        try {
            const r = await api.post(`/clients/${client.id}/workflow-status`, { workflow_status_id: statusId });
            if (r.data?.changed) {
                toast.success(`Status updated to ${r.data.workflow_status_name}`);
                onChanged && onChanged();
            }
            setOpen(false);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setBusy(false); }
    };
    const current = client.workflow_status_name || "Set workflow…";
    const tint = WORKFLOW_MODULE_TINT[client.workflow_status_color] || WORKFLOW_MODULE_TINT.slate;
    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    disabled={busy}
                    className={`group relative h-14 w-full sm:w-[260px] rounded-md border-2 ${tint.frame} pl-3 pr-9 py-1.5 text-left cursor-pointer transition-all hover:shadow-md hover:-translate-y-[1px] focus:outline-none focus:ring-2 focus:ring-primary/40 disabled:opacity-60 disabled:cursor-wait`}
                    data-testid="header-workflow-status-module"
                    data-workflow-status={client.workflow_status_name || ""}
                    data-workflow-color={client.workflow_status_color || "slate"}
                >
                    <div className="flex flex-col leading-tight">
                        <span className="text-[10px] uppercase tracking-wider font-medium opacity-75">
                            Workflow Status
                        </span>
                        <span
                            className={`mt-0.5 inline-flex items-center self-start px-1.5 py-0.5 rounded text-[11px] uppercase tracking-wider font-bold shadow-sm ${tint.pill}`}
                            data-testid="header-workflow-status-current"
                            title={current}
                        >
                            <span className="truncate max-w-[190px]">{current}</span>
                        </span>
                    </div>
                    <span aria-hidden="true" className="absolute right-2.5 top-1/2 -translate-y-1/2 opacity-60 group-hover:opacity-100 transition-opacity">
                        <ChevronDown className="h-4 w-4" />
                    </span>
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-[280px] p-0"
                data-testid="workflow-status-popover"
            >
                <div className="px-3 py-2 border-b bg-muted/40">
                    <div className="text-xs font-semibold text-gray-900">Change Workflow Status</div>
                    <div className="text-[11px] text-muted-foreground">Selecting a new status triggers automation for this client.</div>
                </div>
                <div className="max-h-[320px] overflow-y-auto py-1" data-testid="workflow-status-popover-list">
                    {statuses.map(s => {
                        const isCurrent = s.id === client.workflow_status_id;
                        const rowTint = WORKFLOW_COLOR_CLASS[s.color] || WORKFLOW_COLOR_CLASS.slate;
                        return (
                            <button
                                key={s.id}
                                type="button"
                                disabled={busy}
                                onClick={() => change(s.id)}
                                className={`w-full text-left px-3 py-2 text-sm flex items-center justify-between gap-2 hover:bg-muted/60 focus:bg-muted/60 focus:outline-none ${isCurrent ? "bg-muted/40" : ""}`}
                                data-testid={`workflow-status-option-${s.id}`}
                            >
                                <span className={`inline-flex items-center px-1.5 py-0.5 rounded border text-[10px] uppercase tracking-wider font-semibold ${rowTint}`}>
                                    {s.name}
                                </span>
                                {isCurrent && <Check className="h-3.5 w-3.5 text-primary shrink-0" aria-label="current" />}
                            </button>
                        );
                    })}
                </div>
            </PopoverContent>
        </Popover>
    );
}


// ── Payment Status badge — read-only header pill ──────────────────────
// Problem states (nsf / invalid_card / chargeback) stand out with warm
// borders + iconography; Active is subdued so it stays visually calm.
const PAYMENT_BADGE_CLS = {
    active:       "bg-emerald-50 text-emerald-800 border-emerald-200",
    nsf:          "bg-amber-50  text-amber-900  border-amber-300",
    invalid_card: "bg-rose-50   text-rose-800   border-rose-300",
    chargeback:   "bg-rose-100  text-rose-900   border-rose-400",
};
function PaymentStatusBadge({ value }) {
    const label = PAYMENT_STATUSES.find(p => p.value === value)?.label || value;
    const cls = PAYMENT_BADGE_CLS[value] || PAYMENT_BADGE_CLS.active;
    const problem = value !== "active";
    return (
        <span
            className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-xs font-medium ${cls} ${problem ? "shadow-sm" : ""}`}
            data-testid="header-payment-status-badge"
            data-payment-status={value}
            title={`Payment Status: ${label}`}
        >
            {problem
                ? <AlertTriangle className="h-3 w-3" aria-hidden="true" />
                : <CreditCard className="h-3 w-3" aria-hidden="true" />}
            Payment Status: <span className="font-semibold">{label}</span>
        </span>
    );
}

// ── Case Team popover — Processor (required) + Sales Rep (optional) ───
function CaseTeamControl({ client, onSaved }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(false);
    // Local draft so cancel doesn't touch anything and Save is one atomic PUT.
    const [draft, setDraft] = useState({
        processor_user_id: client.processor_user_id || null,
        processor_name:    client.processor_name    || "",
        processor_email:   client.processor_email   || "",
        sales_rep_user_id: client.sales_rep_user_id || null,
        sales_rep_name:    client.sales_rep_name    || "",
        sales_rep_email:   client.sales_rep_email   || "",
    });
    // When opening, re-sync draft to latest client state.
    useEffect(() => {
        if (open) {
            setDraft({
                processor_user_id: client.processor_user_id || null,
                processor_name:    client.processor_name    || "",
                processor_email:   client.processor_email   || "",
                sales_rep_user_id: client.sales_rep_user_id || null,
                sales_rep_name:    client.sales_rep_name    || "",
                sales_rep_email:   client.sales_rep_email   || "",
            });
        }
    }, [open, client.processor_user_id, client.processor_name, client.processor_email,
        client.sales_rep_user_id, client.sales_rep_name, client.sales_rep_email]);

    const proc = client.processor_name || null;
    const rep  = client.sales_rep_name || null;
    const summary = proc
        ? (rep ? `${proc} / ${rep}` : `${proc} / No Sales Rep`)
        : (rep ? `No Processor / ${rep}` : "Assign…");
    const needsProcessor = !proc;

    const save = async () => {
        if (!draft.processor_user_id) {
            toast.error("Processor is required");
            return;
        }
        setBusy(true);
        try {
            // Use the same full-client PUT pattern that the rest of this
            // page uses (see `persist` at line ~117) so we match backend
            // validator expectations. Only assignment fields differ.
            await api.put(`/clients/${client.id}`, {
                ...client,
                processor_user_id: draft.processor_user_id,
                processor_name:    draft.processor_name,
                processor_email:   draft.processor_email,
                sales_rep_user_id: draft.sales_rep_user_id,
                sales_rep_name:    draft.sales_rep_name,
                sales_rep_email:   draft.sales_rep_email,
            });
            toast.success("Saved");
            setOpen(false);
            onSaved && onSaved();
        } catch (e) {
            toast.error(formatApiError(e) || "Failed to save assignment");
        } finally {
            setBusy(false);
        }
    };

    return (
        <Popover open={open} onOpenChange={setOpen}>
            <PopoverTrigger asChild>
                <button
                    type="button"
                    className={`group relative h-14 w-full sm:w-[260px] rounded-md border pl-3 pr-9 py-1.5 text-left cursor-pointer transition-all hover:shadow-sm hover:brightness-[0.98] focus:outline-none focus:ring-2 focus:ring-primary/30 ${needsProcessor ? "border-l-4 border-amber-400 bg-amber-50 text-amber-900" : "border-l-4 border-primary/40 bg-primary/5 text-gray-900"}`}
                    data-testid="header-case-team-trigger"
                    data-processor-assigned={proc ? "true" : "false"}
                >
                    <div className="flex flex-col leading-tight">
                        <span className="text-[10px] uppercase tracking-wider font-medium opacity-70 inline-flex items-center gap-1.5">
                            <Users className="h-3 w-3" /> Case Team
                        </span>
                        <span
                            className="text-sm font-medium truncate"
                            data-testid="header-case-team-summary"
                            title={summary}
                        >
                            {summary}
                        </span>
                    </div>
                    <span aria-hidden="true" className="absolute right-2.5 top-1/2 -translate-y-1/2 opacity-60 group-hover:opacity-100 transition-opacity">
                        <ChevronDown className="h-4 w-4" />
                    </span>
                </button>
            </PopoverTrigger>
            <PopoverContent
                align="end"
                className="w-[300px] p-4"
                data-testid="case-team-popover"
            >
                <div className="space-y-3">
                    <div>
                        <div className="text-sm font-semibold text-gray-900">Case Team</div>
                        <div className="text-xs text-muted-foreground">Assign Processor and Sales Rep for this client.</div>
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-700">
                            Processor <span className="text-rose-600">*</span>
                        </label>
                        <UserAssignSelect
                            value={draft.processor_user_id}
                            onSelect={(u) => setDraft(d => ({
                                ...d,
                                processor_user_id: u?.id || null,
                                processor_name:    u?.name || "",
                                processor_email:   u?.email || "",
                            }))}
                            placeholder="Select a processor…"
                            testId="case-team-processor-select"
                            allowUnassigned={false}
                            triggerClassName="h-9 text-sm w-full"
                        />
                    </div>
                    <div className="space-y-1.5">
                        <label className="text-xs font-medium text-gray-700">Sales Rep</label>
                        <UserAssignSelect
                            value={draft.sales_rep_user_id}
                            onSelect={(u) => setDraft(d => ({
                                ...d,
                                sales_rep_user_id: u?.id || null,
                                sales_rep_name:    u?.name || "",
                                sales_rep_email:   u?.email || "",
                            }))}
                            placeholder="Optional — select a sales rep…"
                            testId="case-team-sales-rep-select"
                            triggerClassName="h-9 text-sm w-full"
                        />
                    </div>
                    <div className="flex justify-end gap-2 pt-1">
                        <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => setOpen(false)}
                            data-testid="case-team-cancel"
                            disabled={busy}
                        >
                            Cancel
                        </Button>
                        <Button
                            size="sm"
                            onClick={save}
                            disabled={busy || !draft.processor_user_id}
                            data-testid="case-team-save"
                        >
                            {busy ? "Saving…" : "Save"}
                        </Button>
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
}
